import { supabase } from '../lib/supabase'

/**
 * Check if a user has scanning permission based on their team hierarchy
 * 
 * Logic:
 * 1. Get all groups the user is assigned to (from group_members)
 * 2. For each group, check if it OR any parent group has has_scan_permission = true
 * 3. If any team (depth 1) in the hierarchy has scanning permission, user can scan
 * 
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} - True if user can scan, false otherwise
 */
export async function canUserScan(userId) {
  if (!userId) return false

  try {
    // Get user's group memberships
    const { data: memberships, error: memberError } = await supabase
      .from('group_members')
      .select('group_id')
      .eq('user_id', userId)

    if (memberError) {
      console.error('Error fetching group memberships:', memberError)
      return false
    }

    if (!memberships || memberships.length === 0) {
      // User not assigned to any group
      return false
    }

    // Get all group IDs user is in
    const groupIds = memberships.map(m => m.group_id)

    // Get details of these groups
    const { data: userGroups, error: groupError } = await supabase
      .from('groups')
      .select('id, parent_id, depth, has_scan_permission')
      .in('id', groupIds)

    if (groupError) {
      console.error('Error fetching user groups:', groupError)
      return false
    }

    // Get user's org_id from first group
    const { data: firstGroup } = await supabase
      .from('groups')
      .select('org_id')
      .eq('id', groupIds[0])
      .single()

    if (!firstGroup) return false

    // Get ALL groups in the organization to build hierarchy
    const { data: allGroups, error: allGroupsError } = await supabase
      .from('groups')
      .select('id, parent_id, depth, has_scan_permission')
      .eq('org_id', firstGroup.org_id)

    if (allGroupsError) {
      console.error('Error fetching all groups:', allGroupsError)
      return false
    }

    // Helper function to check if a group or any parent has scan permission
    function hasPermissionInHierarchy(groupId, allGroups) {
      const group = allGroups.find(g => g.id === groupId)
      if (!group) return false

      // Check if this group has permission
      if (group.has_scan_permission === true) {
        return true
      }

      // If this is a team (depth 1), no more parents to check
      if (group.depth === 1) {
        return false
      }

      // Check parent
      if (group.parent_id) {
        return hasPermissionInHierarchy(group.parent_id, allGroups)
      }

      return false
    }

    // Check if any of the user's groups (or their parents) have scan permission
    for (const userGroup of userGroups) {
      if (hasPermissionInHierarchy(userGroup.id, allGroups)) {
        return true
      }
    }

    return false

  } catch (error) {
    console.error('Error checking scan permission:', error)
    return false
  }
}

/**
 * Check if a specific group has scanning permission (including inherited from parent)
 * 
 * @param {string} groupId - The group's ID
 * @returns {Promise<boolean>} - True if group has scan permission, false otherwise
 */
export async function groupHasScanPermission(groupId) {
  if (!groupId) return false

  try {
    const { data: group, error } = await supabase
      .from('groups')
      .select('id, parent_id, depth, has_scan_permission, org_id')
      .eq('id', groupId)
      .single()

    if (error || !group) {
      console.error('Error fetching group:', error)
      return false
    }

    // Get all groups in org
    const { data: allGroups, error: allGroupsError } = await supabase
      .from('groups')
      .select('id, parent_id, depth, has_scan_permission')
      .eq('org_id', group.org_id)

    if (allGroupsError) return false

    // Check hierarchy
    function hasPermission(currentGroupId) {
      const currentGroup = allGroups.find(g => g.id === currentGroupId)
      if (!currentGroup) return false

      if (currentGroup.has_scan_permission === true) {
        return true
      }

      if (currentGroup.depth === 1) {
        return false
      }

      if (currentGroup.parent_id) {
        return hasPermission(currentGroup.parent_id)
      }

      return false
    }

    return hasPermission(groupId)

  } catch (error) {
    console.error('Error checking group scan permission:', error)
    return false
  }
}